import { PartyType } from "Api/Api";
import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { MerchantSkeleton } from "Components/Hunter/Merchants/Detail/Tabs/Merchant/MerchantSkeleton";
import { RelatedPartiesSection } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartiesSection";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { ContactPersons } from "Components/Shared/Merchant/ContactPersons/ContactPersons";
import { Facilities } from "Components/Shared/Merchant/Facilities/Facilities";
import { ValuesBox } from "Components/Shared/ValuesBox/ValuesBox";
import { Resources, useResource } from "Translations/Resources";
import { Divider, Stack, Typography } from "@mui/material";
import { LicenseShares } from "Components/Hunter/Merchants/Detail/Tabs/Merchant/LicenseShares";
import { MerchantBasicInfoSection } from "Components/Hunter/Merchants/Detail/MerchantBasicInfoSection";
import type { MerchantDetailDto } from "Infrastructure/Api/Api";

const RESOURCES = Resources.Hunter.Merchants.CompleteRegistration.Notes;

type Props = {
  merchant: MerchantDetailDto;
};

export const ReadOnlyMerchant = ({
  merchant: {
    merchantUserLogin,
    facilities,
    note,
    licenseShares,
    publicID: merchantPublicID,
    party: { publicID: publicPartyID },
  },
}: Props) => {
  const { data: partyData, isLoading: isPartyLoading } =
    usePartyQuery(publicPartyID);
  const { t } = useResource();

  if (isPartyLoading) {
    return <MerchantSkeleton />;
  }

  if (!partyData || !publicPartyID) {
    return (
      <ErrorText>{t(Resources.Components.Merchant.LoadingError)}</ErrorText>
    );
  }

  const isLegalEntity = partyData.type === PartyType.LegalEntity;
  const hasSharedCommission = licenseShares.length > 1;

  return (
    <Stack gap={8}>
      <MerchantBasicInfoSection
        partyData={partyData}
        merchantUserLogin={merchantUserLogin || "-"}
        merchantPublicID={merchantPublicID}
      />
      <Stack>
        <ContactPersons isReadOnly publicPartyID={publicPartyID} />
        <Facilities
          isReadOnly
          merchantPublicID={merchantPublicID}
          facilities={facilities}
        />
        {isLegalEntity && (
          <RelatedPartiesSection
            relatedParties={partyData.relatedParties}
            isReadOnly
          />
        )}
        {note && (
          <>
            <Divider sx={{ mt: [4, 8], mb: 4 }} />
            <ValuesBox title={t(RESOURCES.Title)}>
              <Typography
                fontWeight={400}
                fontSize="1rem"
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
              >
                {note}
              </Typography>
            </ValuesBox>
          </>
        )}
        {hasSharedCommission && (
          <>
            <Divider sx={{ mt: [4, 8], mb: 4 }} />
            <LicenseShares licenseShares={licenseShares} />
          </>
        )}
      </Stack>
    </Stack>
  );
};
